import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import SearchIcon from '@mui/icons-material/Search';
import ExamForm from '../../components/Forms/ExamForm';
import { examService, sessionService } from '../../services/apiService';
import { showToast } from '../../utils/toast';
import { formatDate } from '../../utils/dateFormat';
import { getAvatarColor } from '../../utils/avatar';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationDialog from '../../components/Common/ConfirmationDialog';

const Exams = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const drawerWidth = 280;

  // State management
  const [exams, setExams] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    exam: null,
  });
  
  // Refs to prevent duplicate API calls
  const fetchingSessionRef = useRef(false);

  // Fetch active session
  const fetchActiveSession = useCallback(async () => {
    // Prevent duplicate calls
    if (fetchingSessionRef.current) {
      return;
    }
    
    fetchingSessionRef.current = true;
    try {
      const response = await sessionService.getActive();
      const sessions = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
      const active = sessions.find(s => s.is_active) || sessions[0] || null;
      setActiveSession(active);
    } catch (err) {
      console.error('Error fetching active session:', err);
      // Don't set error state, just log it - session name is optional
    } finally {
      fetchingSessionRef.current = false;
    }
  }, []);

  // Fetch exams from API
  const fetchExams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await examService.getAll();
      
      if (response.success) {
        const data = response.data || [];
        setExams(data);
        setFiltered(data);
      } else {
        const errorMsg = response.error || 'Failed to fetch exams';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error fetching exams:', err);
      const errorMsg = err.message || 'Failed to fetch exams';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    fetchActiveSession();
    fetchExams();
  }, [fetchActiveSession, fetchExams]);

  const handleAdd = async () => {
    setEditingExam(null);
    setModalOpen(true);
  };

  const filteredExams = filtered.filter((exam) => {
    if (!searchText.trim()) return true;
    const query = searchText.toLowerCase();
    return (
      (exam.name || '').toLowerCase().includes(query) ||
      (formatDate(exam.start_date) || '').toLowerCase().includes(query) ||
      (formatDate(exam.end_date) || '').toLowerCase().includes(query)
    );
  });

  const handleEdit = async (exam) => {
    setEditingExam(exam);
    setModalOpen(true);
  };

  const handleDeleteClick = (exam) => {
    setConfirmDialog({
      open: true,
      exam: exam,
    });
  };

  const handleDeleteConfirm = async () => {
    const exam = confirmDialog.exam;
    if (!exam) return;

    try {
      // Don't set loading to true to keep the list visible
      const response = await examService.delete(exam.id);
      
      // Check if response indicates success
      if (response && response.success) {
        showToast.success(`Exam "${exam.name}" deleted successfully`);
        // Clear any previous errors
        setError(null);
        // Refresh the exams list after successful deletion
        await fetchExams();
      } else {
        // Handle error response from API
        const errorMsg = response?.error || response?.message || 'Failed to delete exam';
        setError(errorMsg);
        showToast.error(errorMsg);
      }
    } catch (err) {
      console.error('Error deleting exam:', err);
      // Extract error message from API response
      let errorMsg = 'Failed to delete exam';
      
      if (err.response?.data) {
        // API returned structured error
        errorMsg = err.response.data.error || err.response.data.message || errorMsg;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      // Set error state to show the error message above the list
      setError(errorMsg);
      showToast.error(errorMsg);
    }
  };

  // Modal handlers
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingExam(null);
  };

  const handleModalSave = useCallback(async (formData) => {
    try {
      setModalLoading(true);
      
      if (editingExam) {
        // Update existing exam
        await examService.update(editingExam.id, formData);
        showToast.success(`Exam "${formData.name}" updated successfully`);
      } else {
        // Create new exam
        await examService.create(formData);
        showToast.success(`Exam "${formData.name}" created successfully`);
      }
      
      // Refresh the exams list after successful create/update
      await fetchExams();
      
      // Close modal
      handleModalClose();
    } catch (err) {
      console.error('Error saving exam:', err);
      const errorMsg = err.message || 'Failed to save exam';
      setError(errorMsg);
      showToast.error(errorMsg);
    } finally {
      setModalLoading(false);
    }
  }, [editingExam, fetchExams]);


  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar 
          open={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          drawerWidth={drawerWidth} 
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            // On mobile: always full width, no margin (sidebar overlays)
            // On desktop: adjust width and margin based on sidebar state
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
                mb: 3,
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="h5" fontWeight={700}>
                      Exams Management
                    </Typography>
                    {activeSession && (
                      <Chip
                        label={'Session:' + ' ' + activeSession.name || activeSession.academic_year || 'Current Session'}
                        size="small"
                        color="primary"
                        sx={{
                          fontWeight: 600,
                          height: 24,
                          '& .MuiChip-label': {
                            px: 1.5,
                            fontSize: '0.75rem',
                          },
                        }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Create, schedule, and monitor exam sessions across the school year.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                  <TextField
                    fullWidth
                    placeholder="Search exams by name or date"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      sx: { height: 40 },
                    }}
                    inputProps={{ sx: { py: 0, height: 40 } }}
                  />
                  <Button
                    variant="contained"
                    sx={{ width: { xs: '100%', md: '150px' }, height: 40, minHeight: 40 }}
                    onClick={handleAdd}
                  >
                    <AddIcon sx={{ mr: 1 }} fontSize="small" /> Add Exam
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </Box>
            ) : filteredExams.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  textAlign: 'center',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {searchText.trim() ? 'No exams match your search.' : 'No exams found. Click "Add Exam" to schedule one.'}
                </Typography>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gap: { xs: 1.5, sm: 2 },
                  gridTemplateColumns: '1fr',
                }}
              >
                {filteredExams.map((exam) => (
                  <Paper
                    key={exam.id}
                    variant="outlined"
                    sx={{
                      p: { xs: 2, sm: 2.4 },
                      borderRadius: 2,
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      position: 'relative',
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                      {/* Left: Image + Details */}
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          component="img"
                          src="/icons/exam.png"
                          alt="Exam"
                          sx={{
                            width: 36,
                            height: 36,
                            objectFit: 'contain',
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={700} noWrap>
                            {exam.name || 'Untitled Exam'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            <strong>Starts:</strong> {formatDate(exam.start_date) || '—'} &nbsp;·&nbsp; <strong>Ends:</strong> {formatDate(exam.end_date) || '—'}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Right: Actions - match Students */}
                      <Stack direction={{ xs: 'row', sm: 'column' }} spacing={1} alignItems={{ xs: 'center', sm: 'flex-end' }} justifyContent={{ xs: 'flex-end', sm: 'flex-start' }} sx={{ flex: '0 0 auto', minWidth: { xs: 'auto', sm: '100px' } }}>
                        <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(exam)}>
                          Edit
                        </Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteClick(exam)}>
                          Delete
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Exam Form Modal */}
            <ExamForm
              open={modalOpen}
              onClose={handleModalClose}
              onSubmit={handleModalSave}
              initialData={editingExam}
              title={editingExam ? 'Edit Exam' : 'Add New Exam'}
            />

            {/* Confirmation Dialog */}
            <ConfirmationDialog
              open={confirmDialog.open}
              onClose={() => setConfirmDialog({ open: false, exam: null })}
              onConfirm={handleDeleteConfirm}
              title="Delete Exam"
              message={confirmDialog.exam ? `Are you sure you want to delete "${confirmDialog.exam.name}"? This action cannot be undone.` : ''}
              confirmText="Delete"
              cancelText="Cancel"
              confirmColor="error"
              severity="error"
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Exams;

